import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { useSelector } from 'react-redux';
import { RootState } from '../store';
import { clearRolePreviewStorage, ROLE_PREVIEW_STORAGE_KEY } from '../utils/rolePreviewStorage';

/** IC console RBAC roles a superadmin can preview as (UI only). */
export const PREVIEWABLE_ROLES = [
  'superadmin',
  'itadmin',
  'itoperator',
  'approver',
  'observer',
  'auditor',
] as const;

export type PreviewableRole = (typeof PREVIEWABLE_ROLES)[number];

type RolePreviewContextValue = {
  /** Authenticated role from JWT / Redux (never faked). */
  realRole: string | undefined;
  /** Role used for nav / ProtectedRoute / canWrite UI. */
  effectiveRole: string | undefined;
  previewRole: PreviewableRole | null;
  isPreviewing: boolean;
  canUseRolePreview: boolean;
  setPreviewRole: (role: PreviewableRole | null) => void;
  clearPreview: () => void;
  /** True when effectiveRole is allowed to perform UI writes for workspace admin. */
  canWriteWorkspace: boolean;
};

const RolePreviewContext = createContext<RolePreviewContextValue | null>(null);

function readStoredPreview(): PreviewableRole | null {
  try {
    const raw = sessionStorage.getItem(ROLE_PREVIEW_STORAGE_KEY);
    if (!raw) return null;
    if ((PREVIEWABLE_ROLES as readonly string[]).includes(raw)) {
      return raw as PreviewableRole;
    }
  } catch {
    /* ignore */
  }
  return null;
}

export const RolePreviewProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const user = useSelector((s: RootState) => s.auth.user);
  const realRole = user?.role as string | undefined;
  const canUseRolePreview = realRole === 'superadmin';

  const [previewRole, setPreviewRoleState] = useState<PreviewableRole | null>(() => readStoredPreview());

  useEffect(() => {
    if (!canUseRolePreview && previewRole) {
      setPreviewRoleState(null);
      clearRolePreviewStorage();
    }
  }, [canUseRolePreview, previewRole]);

  const setPreviewRole = useCallback(
    (role: PreviewableRole | null) => {
      if (!canUseRolePreview) return;
      if (role === null || role === 'superadmin') {
        setPreviewRoleState(null);
        clearRolePreviewStorage();
        return;
      }
      setPreviewRoleState(role);
      try {
        sessionStorage.setItem(ROLE_PREVIEW_STORAGE_KEY, role);
      } catch {
        /* ignore */
      }
    },
    [canUseRolePreview],
  );

  const clearPreview = useCallback(() => setPreviewRole(null), [setPreviewRole]);

  const effectiveRole = canUseRolePreview && previewRole ? previewRole : realRole;
  const isPreviewing = Boolean(canUseRolePreview && previewRole);

  const value = useMemo<RolePreviewContextValue>(
    () => ({
      realRole,
      effectiveRole,
      previewRole: canUseRolePreview ? previewRole : null,
      isPreviewing,
      canUseRolePreview,
      setPreviewRole,
      clearPreview,
      canWriteWorkspace: effectiveRole === 'itadmin' || effectiveRole === 'superadmin',
    }),
    [
      realRole,
      effectiveRole,
      previewRole,
      isPreviewing,
      canUseRolePreview,
      setPreviewRole,
      clearPreview,
    ],
  );

  return <RolePreviewContext.Provider value={value}>{children}</RolePreviewContext.Provider>;
};

export function useRolePreview(): RolePreviewContextValue {
  const ctx = useContext(RolePreviewContext);
  if (!ctx) {
    throw new Error('useRolePreview must be used within RolePreviewProvider');
  }
  return ctx;
}

export function useRolePreviewOptional(): RolePreviewContextValue | null {
  return useContext(RolePreviewContext);
}
